import { Box, InputAdornment, Stack, TextField, useTheme } from '@mui/material';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { SearchFillIcon } from 'src/assets/icons/SearchFillIcon';
import { CustomWaterLoading } from 'src/components';
import { useAuthentication } from 'src/features/authentication/context';
import { TagData } from 'src/features/reports/types';
import { ModalContentItem } from 'src/features/trends/components';
import { SelectedItemsList } from 'src/features/trends/components/selected-items-list';
import { useDebounce } from 'src/hooks/useDebounce';

interface ITagSelectionInputProps {
  value?: TagData;
  onChange: (tag: TagData | null) => void;
}

const TagSelectionInput: FunctionComponent<ITagSelectionInputProps> = ({ onChange, value }) => {
  const methods = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const values = methods.watch();
  const { sites } = useAuthentication();

  const [filteredItems, setFilteredItems] = useState<any>([]);

  useEffect(() => {
    if (value && sites.value.length) {
      methods.setValue(
        `row(sep)${value.siteId}(sep)${value.siteName}(sep)${value.rawUnit}(sep)${value.gatewayId}(sep)${value.tagId}(sep)${value.tagName}`,
        'true'
      );
    }
  }, [value, methods, sites]);

  const debouncedSearchTerm = useDebounce(values?.search, 300);
  const sitesForDisplay = debouncedSearchTerm ? filteredItems : sites?.value;
  useEffect(() => {
    const filteredItems = sites?.value.filter((tag: any) =>
      tag.siteName?.toLowerCase().includes(debouncedSearchTerm)
    );
    if (filteredItems) setFilteredItems(filteredItems);
    setIsLoading(false);
  }, [debouncedSearchTerm, sites]);

  const setValue = useCallback(
    (name: any, value: any) => {
      methods.setValue(name, value);
      const tagSplit = name.split('(sep)');
      const tagData = {
        siteId: tagSplit[1],
        siteName: tagSplit[2],
        rawUnit: [null, '', 'null'].includes(tagSplit[4]) ? '' : tagSplit[4],
        gatewayId: tagSplit[5],
        tagId: tagSplit[6],
        tagName: tagSplit[7],
      };
      onChange(tagData);
    },
    [onChange, methods]
  );

  const valuesWithoutSearch = Object.fromEntries(
    Object.entries(values || {}).filter(([key]: any) => key !== 'search')
  );

  const canChange =
    !Object.values(valuesWithoutSearch || {}).length ||
    !Object.values(valuesWithoutSearch).filter((val: any) => val !== 'false').length;

  return (
    <FormProvider {...methods}>
      <Stack sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '10px',
            padding: '10px 20px',
          }}
        >
          <>
            {canChange && (
              <TextField
                onChange={(event) => {
                  methods.setValue('search', event.target.value);
                  setIsLoading(true);
                }}
                value={methods.watch('search') ?? ''}
                name="search"
                fullWidth
                placeholder="Search sites..."
                inputProps={{ style: { padding: '10px' }, 'data-sm': 'search-sites' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchFillIcon fill={theme.palette.text.disabled} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <SelectedItemsList
              single
              values={values}
              setValue={(name, value) => {
                onChange(null);
                methods.setValue(name, value);
              }}
              sites={sites?.value}
            />
          </>
        </Box>

        {canChange ? (
          !sites?.loaded || isLoading ? (
            <Stack
              flexDirection={'row'}
              justifyContent={'center'}
              alignItems={'center'}
              sx={{ mb: '16px' }}
            >
              <CustomWaterLoading />
            </Stack>
          ) : (
            <>
              <Box
                sx={{
                  maxHeight: '450px',
                  overflow: 'visible',
                  gap: '5px',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '0 20px',
                }}
              >
                {sitesForDisplay?.map((site: any) => (
                  <ModalContentItem
                    key={site.id}
                    site={site}
                    disabled={!!Object.values(values).filter((value) => value === 'true').length}
                    setValue={setValue}
                    values={values}
                  />
                ))}
              </Box>
            </>
          )
        ) : (
          <></>
        )}
      </Stack>
    </FormProvider>
  );
};

export default React.memo(TagSelectionInput);
